<template>
  <div class="staff-page">
    <el-card class="tool-card" shadow="never">
      <div class="tool-row">
        <div class="tool-left">
          <span class="tool-title">员工管理</span>
          <span class="tool-sub">员工隶属战队/小组，销售/推广按所属战队类型判定</span>
        </div>
        <div class="tool-right">
          <template v-if="!isTeam">
            <el-select v-if="!isUnit" v-model="filterUnitId" placeholder="全部战队" clearable style="width: 160px" @change="onUnitFilter">
              <el-option v-for="u in units" :key="u.id" :label="u.name" :value="u.id" />
            </el-select>
            <el-select v-model="filterTeamId" placeholder="全部小组" clearable style="width: 160px" @change="load">
              <el-option v-for="t in teams" :key="t.id" :label="t.name" :value="t.id" />
            </el-select>
          </template>
          <el-button type="primary" icon="el-icon-plus" @click="openEdit(null)">新增员工</el-button>
        </div>
      </div>
    </el-card>

    <el-card class="table-card" shadow="never">
      <el-table
        v-loading="loading"
        :data="list"
        :header-cell-style="{ background: '#F7F9FC', color: '#18233C', fontWeight: 600 }"
        empty-text="暂无员工数据"
      >
        <!-- 第一列 员工 -->
        <el-table-column label="员工" min-width="180">
          <template slot-scope="{ row }">
            <div class="cell-name-main">
              <div class="cell-name-text">
                {{ row.name }}
                <el-tag v-if="parseInt(row.is_leader) === 1" type="warning" size="mini" effect="plain" style="margin-left: 4px;">队长</el-tag>
              </div>
              <div class="cell-name-sub">{{ row.account }}</div>
              <div class="cell-name-meta">
                <span>{{ row.unit_name || unitName(row.unit_id) }}</span>
                <i class="el-icon-arrow-right cell-name-arrow"></i>
                <span>{{ row.team_name || teamName(row.team_id) }}</span>
              </div>
            </div>
          </template>
        </el-table-column>
        <!-- 第二列 类型 -->
        <el-table-column label="类型" min-width="80" align="center">
          <template slot-scope="{ row }">
            <el-tag :type="parseInt(row.type) === 2 ? 'primary' : 'success'" size="small">{{ row.type_text }}</el-tag>
          </template>
        </el-table-column>
        <!-- 今日接单（销售员工：已接/每日可接；推广员工：—） -->
        <el-table-column label="今日接单" min-width="110" align="center">
          <template slot-scope="{ row }">
            <span v-if="parseInt(row.type) === 2" class="today-count">
              <b>{{ row.today_count || 0 }}</b>
              <span class="today-slash">/</span>
              {{ row.daily_limit || 0 }}
            </span>
            <span v-else class="today-none">—</span>
          </template>
        </el-table-column>
        <!-- 是否接受推广（开关，控制是否进入推广推单池；销售员工可设） -->
        <el-table-column label="接受推广" min-width="110" align="center">
          <template slot-scope="{ row }">
            <el-switch
              v-if="parseInt(row.type) === 2"
              :value="parseInt(row.flow_accept) === 1"
              active-color="#16A34A"
              inactive-color="#C3CADB"
              @change="(v) => onFlowAcceptChange(row, v)"
            />
            <span v-else class="today-none">—</span>
          </template>
        </el-table-column>
        <!-- 每日接受推广上限（复用配置流量，销售员工可设，0=不限） -->
        <el-table-column label="推广上限" min-width="150" align="center">
          <template slot-scope="{ row }">
            <div v-if="parseInt(row.type) === 2" class="promo-limit-cell">
              <el-input-number
                :value="row.daily_limit"
                :min="0"
                :max="999"
                :controls="false"
                size="small"
                style="width: 84px"
                title="每日接单上限，即配置流量中的每日上限，0=不限"
                @change="(v) => onDailyLimitChange(row, v)"
              />
              <span class="promo-limit-flag" v-if="parseInt(row.daily_limit) === 0">不限</span>
            </div>
            <span v-else class="today-none">—</span>
          </template>
        </el-table-column>
        <!-- 工作状态（状态标签 + 开关） -->
        <el-table-column label="工作状态" min-width="140" align="center">
          <template slot-scope="{ row }">
            <div class="work-status-cell">
              <el-tag :type="parseInt(row.work_status) === 1 ? 'success' : 'info'" size="mini" effect="plain">
                {{ parseInt(row.work_status) === 1 ? '正常' : '休息' }}
              </el-tag>
              <el-switch
                :value="parseInt(row.work_status) === 1"
                active-color="#16A34A"
                inactive-color="#C3CADB"
                @change="(v) => onWorkStatusChange(row, v)"
              />
            </div>
          </template>
        </el-table-column>
        <!-- 第四列 创建时间 -->
        <el-table-column label="创建时间" min-width="150">
          <template slot-scope="{ row }">
            <span class="cell-time">{{ fmtDateTime(row.createtime) }}</span>
          </template>
        </el-table-column>
        <!-- 第五列 操作 -->
        <el-table-column label="操作" width="360" align="center">
          <template slot-scope="{ row }">
            <el-button type="text" size="small" @click="openEdit(row)">编辑</el-button>
            <el-button v-if="parseInt(row.type) === 2" type="text" size="small" @click="openQrcode(row)">二维码</el-button>
            <el-button v-if="!isTeam" type="text" size="small" @click="openGroup(row)">更改小组</el-button>
            <el-button type="text" size="small" @click="openAccount(row)">重置账号</el-button>
            <el-button type="text" size="small" @click="openPassword(row)">重置密码</el-button>
            <el-button type="text" size="small" :class="parseInt(row.status) === 0 ? 'en-btn' : 'del-btn'" @click="doToggleStatus(row)">
              {{ parseInt(row.status) === 0 ? '启用' : '停用' }}
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- 新增/编辑员工弹窗 -->
    <el-dialog :title="editId ? '编辑员工' : '新增员工'" :visible.sync="editVisible" width="520px">
      <el-form ref="form" :model="form" :rules="rules" label-width="88px" size="small">
        <el-form-item label="所属战队" prop="unit_id" v-if="!isTeam">
          <el-select v-model="form.unit_id" placeholder="请选择战队" style="width: 100%" :disabled="isUnit" @change="onUnitChange">
            <el-option v-for="u in units" :key="u.id" :label="u.name + '（' + u.type_text + '）'" :value="u.id" />
          </el-select>
        </el-form-item>
        <el-form-item label="所属小组" prop="team_id" v-if="!isTeam">
          <el-select v-model="form.team_id" placeholder="请选择小组" style="width: 100%">
            <el-option v-for="t in unitTeams" :key="t.id" :label="t.name" :value="t.id" />
          </el-select>
        </el-form-item>
        <el-form-item v-if="isTeam" label="">
          <span class="form-hint">新组员将加入当前小组，归属由系统自动确定</span>
        </el-form-item>
        <el-form-item label="登录账号" prop="account">
          <el-input v-model="form.account" placeholder="3-50 位字母数字下划线" :maxlength="50" />
        </el-form-item>
        <el-form-item label="员工姓名" prop="name">
          <el-input v-model="form.name" :maxlength="50" />
        </el-form-item>
        <el-form-item label="手机号">
          <el-input v-model="form.phone" placeholder="11 位手机号" :maxlength="11" />
        </el-form-item>
        <el-form-item label="登录密码" prop="password">
          <el-input v-model="form.password" type="password" show-password :placeholder="editId ? '不修改请留空' : '请设置登录密码'" />
        </el-form-item>
        <el-form-item label="是否队长">
          <el-switch v-model="form.is_leader" active-text="是" inactive-text="否" :disabled="curUnitType !== 2" />
          <div v-if="curUnitType !== 2" class="form-hint">队长标识仅限销售战队的员工</div>
        </el-form-item>
        <el-form-item label="工作状态">
          <el-radio-group v-model="form.work_status">
            <el-radio :label="1">正常</el-radio>
            <el-radio :label="2">休息中</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="备注">
          <el-input v-model="form.remark" type="textarea" :rows="2" :maxlength="200" />
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button size="small" @click="editVisible = false">取 消</el-button>
        <el-button type="primary" size="small" :loading="saving" @click="doSave">保 存</el-button>
      </div>
    </el-dialog>

    <!-- 二维码管理弹窗（仅销售） -->
    <el-dialog :title="'二维码管理 · ' + (curStaff ? curStaff.name : '')" :visible.sync="qrcodeVisible" width="720px">
      <div class="qrcode-note">仅销售员工可上传，支持 QQ / 微信，可添加多个，每个对应账号、状态、备注。</div>
      <div v-loading="qrcodeLoading" class="qrcode-body">
        <div v-if="qrcodeList.length === 0 && !qrcodeLoading" class="empty-row">暂无二维码，点击下方上传区上传</div>
        <div v-for="q in qrcodeList" :key="q.id" class="qrcode-item">
          <div class="qrcode-card">
            <img :src="resolveAsset(q.qrcode)" class="qrcode-img" />
            <div class="qrcode-type" :class="'qtype-' + q.type">{{ q.type_text }}</div>
            <div class="qrcode-account">{{ q.account || '—' }}</div>
            <el-switch
              :value="parseInt(q.status) === 1"
              active-text="启用"
              inactive-text="停用"
              active-color="#16A34A"
              inactive-color="#C3CADB"
              style="margin-top: 6px;"
              @change="(v) => onQrcodeStatusChange(q, v)"
            />
            <div v-if="q.remark" class="qrcode-remark">{{ q.remark }}</div>
          </div>
          <div class="qrcode-ops">
            <el-button type="text" size="small" @click="editQrcode(q)">编辑</el-button>
            <el-button type="text" size="small" class="del-btn" @click="deleteQrcode(q)">删除</el-button>
          </div>
        </div>
      </div>
      <!-- 直接上传区：选类型 + 填账号 + 上传图片即新增一张二维码 -->
      <div class="qrcode-inline-upload">
        <div class="qrcode-inline-form">
          <div class="qrcode-inline-item">
            <span class="qrcode-inline-label">类型</span>
            <el-radio-group v-model="quickQrcodeForm.type" size="mini">
              <el-radio-button :label="2">微信</el-radio-button>
              <el-radio-button :label="1">QQ</el-radio-button>
            </el-radio-group>
          </div>
          <div class="qrcode-inline-item">
            <span class="qrcode-inline-label">账号</span>
            <el-input v-model="quickQrcodeForm.account" placeholder="对应账号（选填）" :maxlength="100" clearable style="width: 220px" />
          </div>
          <div class="qrcode-inline-item">
            <span class="qrcode-inline-label">二维码</span>
            <el-upload
              action="/api/upload/index"
              name="file"
              :headers="uploadHeaders"
              :show-file-list="false"
              accept="image/*"
              :on-success="onQuickQrcodeSuccess"
              :on-error="() => $message.error('上传失败')"
            >
              <el-button type="primary" size="small" icon="el-icon-plus" :loading="quickQrcodeSaving">上传图片</el-button>
            </el-upload>
          </div>
        </div>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button size="small" @click="qrcodeVisible = false">关 闭</el-button>
      </div>
    </el-dialog>

    <!-- 新增/编辑二维码子弹窗 -->
    <el-dialog title="二维码" :visible.sync="qrcodeEditVisible" width="460px" append-to-body>
      <el-form ref="qform" :model="qform" :rules="qrules" label-width="88px" size="small">
        <el-form-item label="类型" prop="type">
          <el-radio-group v-model="qform.type">
            <el-radio :label="1">QQ 二维码</el-radio>
            <el-radio :label="2">微信二维码</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="对应账号" prop="account">
          <el-input v-model="qform.account" placeholder="该二维码对应的账号" :maxlength="100" />
        </el-form-item>
        <el-form-item label="二维码" prop="qrcode">
          <el-upload
            class="qrcode-uploader"
            action="/api/upload/index"
            name="file"
            :headers="uploadHeaders"
            :show-file-list="false"
            :on-success="onQrcodeUploadSuccess"
            :on-error="() => $message.error('上传失败')"
            accept="image/*"
          >
            <img v-if="qform.qrcode" :src="resolveAsset(qform.qrcode)" class="qrcode-upload-preview" />
            <i v-else class="el-icon-plus qrcode-upload-icon"></i>
          </el-upload>
        </el-form-item>
        <el-form-item label="状态">
          <el-switch v-model="qform.status" active-text="启用" inactive-text="停用" />
        </el-form-item>
        <el-form-item label="备注">
          <el-input v-model="qform.remark" :maxlength="255" />
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button size="small" @click="qrcodeEditVisible = false">取 消</el-button>
        <el-button type="primary" size="small" :loading="qrcodeSaving" @click="doQrcodeSave">保 存</el-button>
      </div>
    </el-dialog>

    <!-- 更改小组弹窗 -->
    <el-dialog :title="'更改小组 · ' + (curStaff ? curStaff.name : '')" :visible.sync="groupVisible" width="440px">
      <el-form ref="gform" :model="gform" :rules="grules" label-width="88px" size="small">
        <el-form-item label="所属战队" prop="unit_id">
          <el-select v-model="gform.unit_id" placeholder="请选择战队" style="width: 100%" @change="onGroupUnitChange">
            <el-option v-for="u in units" :key="u.id" :label="u.name" :value="u.id" />
          </el-select>
        </el-form-item>
        <el-form-item label="所属小组" prop="team_id">
          <el-select v-model="gform.team_id" placeholder="请选择小组" style="width: 100%">
            <el-option v-for="t in groupTeams" :key="t.id" :label="t.name" :value="t.id" />
          </el-select>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button size="small" @click="groupVisible = false">取 消</el-button>
        <el-button type="primary" size="small" :loading="saving" @click="doGroupSave">保 存</el-button>
      </div>
    </el-dialog>

    <!-- 重置账号弹窗 -->
    <el-dialog :title="'重置登录账号 · ' + (curStaff ? curStaff.name : '')" :visible.sync="accountVisible" width="440px">
      <el-form ref="rform" :model="rform" :rules="rrules" label-width="88px" size="small">
        <el-form-item label="新账号" prop="account">
          <el-input v-model="rform.account" placeholder="3-50 位字母数字下划线" :maxlength="50" />
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button size="small" @click="accountVisible = false">取 消</el-button>
        <el-button type="primary" size="small" :loading="saving" @click="doAccountSave">保 存</el-button>
      </div>
    </el-dialog>

    <!-- 重置密码弹窗 -->
    <el-dialog :title="'重置密码 · ' + (curStaff ? curStaff.name : '')" :visible.sync="passwordVisible" width="440px">
      <el-form ref="pform" :model="pform" :rules="prules" label-width="88px" size="small">
        <el-form-item label="新密码" prop="password">
          <el-input v-model="pform.password" type="password" show-password placeholder="请输入新密码" />
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button size="small" @click="passwordVisible = false">取 消</el-button>
        <el-button type="primary" size="small" :loading="saving" @click="doPasswordSave">保 存</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  staffList, staffSave, staffDelete, staffWorkStatus, staffDailyLimit, staffFlowAccept,
  staffQrcodeList, staffQrcodeSave, staffQrcodeDelete, staffQrcodeStatus,
  unitList, teamList
} from '@/api/org'
import { API_BASE } from '@/config'

// 后端资源根（API_BASE 去掉 /api）：用于把后端返回的相对路径资源拼成完整可访问地址
const ASSET_BASE = API_BASE.replace(/\/api\/?$/, '')

export default {
  name: 'StaffList',
  data() {
    return {
      list: [],
      units: [],
      teams: [],
      unitTeams: [],
      filterUnitId: null,
      filterTeamId: null,
      loading: false,
      // 编辑
      editVisible: false,
      editId: 0,
      saving: false,
      form: {
        unit_id: null, team_id: null, account: '', name: '', phone: '',
        password: '', is_leader: false, work_status: 1, remark: ''
      },
      rules: {
        unit_id: [{ required: true, message: '请选择所属战队', trigger: 'change' }],
        team_id: [{ required: true, message: '请选择所属小组', trigger: 'change' }],
        account: [
          { required: true, message: '请填写登录账号', trigger: 'blur' },
          { pattern: /^[a-zA-Z0-9_]{3,50}$/, message: '3-50 位字母数字下划线', trigger: 'blur' }
        ],
        name: [{ required: true, message: '请填写员工姓名', trigger: 'blur' }],
        password: [{ validator: (r, v, cb) => { if (this.editId === 0 && !v) cb(new Error('请设置登录密码')); else cb() }, trigger: 'blur' }]
      },
      // 当前操作行
      curStaff: null,
      // 二维码
      qrcodeVisible: false,
      qrcodeLoading: false,
      qrcodeList: [],
      qrcodeEditVisible: false,
      qrcodeSaving: false,
      // 底部直接上传区
      quickQrcodeForm: { type: 2, account: '' },
      quickQrcodeSaving: false,
      qform: { id: 0, type: 1, account: '', qrcode: '', status: true, remark: '' },
      qrules: {
        type: [{ required: true, message: '请选择二维码类型', trigger: 'change' }],
        account: [{ required: true, message: '请填写对应账号', trigger: 'blur' }],
        qrcode: [{ required: true, message: '请上传二维码图片', trigger: 'change' }]
      },
      // 更改小组
      groupVisible: false,
      groupTeams: [],
      gform: { unit_id: null, team_id: null },
      grules: {
        unit_id: [{ required: true, message: '请选择战队', trigger: 'change' }],
        team_id: [{ required: true, message: '请选择小组', trigger: 'change' }]
      },
      // 重置账号
      accountVisible: false,
      rform: { account: '' },
      rrules: {
        account: [
          { required: true, message: '请填写新账号', trigger: 'blur' },
          { pattern: /^[a-zA-Z0-9_]{3,50}$/, message: '3-50 位字母数字下划线', trigger: 'blur' }
        ]
      },
      // 重置密码
      passwordVisible: false,
      pform: { password: '' },
      prules: {
        password: [{ required: true, message: '请输入新密码', trigger: 'blur' }]
      }
    }
  },
  computed: {
    uploadHeaders() {
      const token = this.$store.state.token || localStorage.getItem('crm_token') || ''
      return { Token: token }
    },
    // 小组组长（team）：仅能管理本小组组员
    isTeam() {
      const u = this.$store.state.user
      return !!u && u.type === 'team'
    },
    // 战队长（unit）：仅能管理本战队员工
    isUnit() {
      const u = this.$store.state.user
      return !!u && u.type === 'unit'
    },
    selfUnitId() {
      const u = this.$store.state.user
      return u ? parseInt(u.id) : 0
    },
    selfType() {
      const u = this.$store.state.user
      return u ? parseInt(u.unit_type) : 2
    },
    curUnitType() {
      const u = this.units.find(x => parseInt(x.id) === parseInt(this.form.unit_id))
      return u ? parseInt(u.type) : null
    }
  },
  created() {
    this.loadUnits()
    this.load()
  },
  methods: {
    /* ===== 基础数据 ===== */
    // 后端相对路径资源 → 完整可访问地址
    resolveAsset(url) {
      if (!url) return ''
      if (/^https?:\/\//.test(url)) return url
      if (/^\//.test(url)) return ASSET_BASE + url
      return url
    },
    loadUnits() {
      // 小组组长后端无权查组织列表（unitList/teamList 仅管理员），跳过避免报错
      if (this.isTeam) {
        this.units = []
        this.teams = []
        return
      }
      // 战队长：只归属本战队，小组列表取本战队
      if (this.isUnit) {
        const u = this.$store.state.user
        this.units = [{ id: this.selfUnitId, name: (u && u.name) || '本战队', type: this.selfType || 2 }]
        return teamList({}).then(dt => { this.teams = dt.list || [] }).catch(() => {})
      }
      unitList()
        .then(data => {
          this.units = data.list || []
          return teamList({}).then(dt => { this.teams = dt.list || [] })
        })
        .catch(() => {})
    },
    load() {
      this.loading = true
      staffList({ unit_id: this.isUnit ? this.selfUnitId : (this.filterUnitId || 0), team_id: this.filterTeamId || 0 })
        .then(data => { this.list = data.list || [] })
        .catch(() => { this.list = [] })
        .finally(() => { this.loading = false })
    },
    unitName(unitId) {
      const u = this.units.find(x => parseInt(x.id) === parseInt(unitId))
      return u ? u.name : '—'
    },
    teamName(teamId) {
      const t = this.teams.find(x => parseInt(x.id) === parseInt(teamId))
      return t ? t.name : '—'
    },
    onUnitFilter() {
      this.filterTeamId = null
      this.load()
    },
    onUnitChange() {
      this.form.team_id = null
      const u = this.units.find(x => parseInt(x.id) === parseInt(this.form.unit_id))
      this.unitTeams = u ? this.teams.filter(t => parseInt(t.unit_id) === parseInt(u.id)) : []
      if (u && parseInt(u.type) !== 2) this.form.is_leader = false
    },
    /* ===== 编辑 ===== */
    openEdit(row) {
      if (row) {
        this.editId = row.id
        this.unitTeams = this.teams.filter(t => parseInt(t.unit_id) === parseInt(row.unit_id))
        this.form = {
          unit_id: parseInt(row.unit_id),
          team_id: parseInt(row.team_id),
          account: row.account,
          name: row.name,
          phone: row.phone || '',
          password: '',
          is_leader: parseInt(row.is_leader) === 1,
          work_status: parseInt(row.work_status),
          remark: row.remark || ''
        }
      } else {
        this.editId = 0
        this.unitTeams = this.isUnit ? this.teams.filter(t => parseInt(t.unit_id) === this.selfUnitId) : []
        this.form = {
          unit_id: this.isUnit ? this.selfUnitId : null, team_id: null, account: '', name: '', phone: '',
          password: '', is_leader: false, work_status: 1, remark: ''
        }
      }
      this.editVisible = true
    },
    doSave() {
      this.$refs.form.validate(valid => {
        if (!valid) return
        if (this.form.is_leader && this.curUnitType !== 2) {
          this.$message.error('队长标识仅限销售战队的员工')
          return
        }
        this.saving = true
        const payload = {
          id: this.editId,
          account: this.form.account,
          name: this.form.name,
          phone: (this.form.phone || '').trim(),
          password: this.form.password,
          is_leader: this.form.is_leader ? 1 : 0,
          work_status: this.form.work_status,
          remark: (this.form.remark || '').trim()
        }
        // 机构管理员指定归属；小组组长归属由后端强制为本小组，前端不传
        if (!this.isTeam) {
          payload.unit_id = this.form.unit_id
          payload.team_id = this.form.team_id
        }
        staffSave(payload)
          .then(() => {
            this.$message.success(this.editId ? '修改成功' : '新增成功')
            this.editVisible = false
            this.load()
          })
          .catch(() => {})
          .finally(() => { this.saving = false })
      })
    },
    /* ===== 工作状态开关 ===== */
    onWorkStatusChange(row, val) {
      const workStatus = val ? 1 : 2
      staffWorkStatus({ id: row.id, work_status: workStatus })
        .then(() => {
          row.work_status = workStatus
          row.work_status_text = workStatus === 1 ? '正常' : '休息中'
          this.$message.success(workStatus === 1 ? '已设为正常' : '已设为休息')
        })
        .catch(() => {
          // 失败时回滚开关状态
          this.$forceUpdate()
        })
    },
    /* ===== 每日接受推广上限（复用配置流量） ===== */
    onDailyLimitChange(row, val) {
      const v = val == null ? 0 : parseInt(val)
      if (v < 0 || v > 999) {
        this.$message.error('每日上限须在 0-999 之间')
        this.$forceUpdate()
        return
      }
      staffDailyLimit({ id: row.id, daily_promo_limit: v })
        .then(() => {
          this.$message.success(v === 0 ? '已设为不限' : `上限已设为 ${v}`)
          if (v !== parseInt(row.daily_limit)) this.load()
        })
        .catch(() => { this.$forceUpdate() })
    },
    /* ===== 是否接受推广（复用配置流量状态） ===== */
    onFlowAcceptChange(row, val) {
      const accept = val ? 1 : 0
      staffFlowAccept({ id: row.id, accept })
        .then(res => {
          this.$message.success((res && res.msg) || (accept ? '已开启接收推广' : '已暂停接收推广'))
          this.load()
        })
        .catch(() => { this.$forceUpdate() })
    },
    /* ===== 停用/启用（不物理删除） ===== */
    doToggleStatus(row) {
      const act = parseInt(row.status) === 0 ? '启用' : '停用'
      const tip = act === '停用'
        ? `停用后「${row.name}」将无法登录，数据全部保留，可随时重新启用。`
        : `确认启用「${row.name}」？`
      this.$confirm(`确认${act}员工「${row.name}」？${tip}`, act, {
        confirmButtonText: '确 认',
        cancelButtonText: '取 消',
        type: 'warning'
      }).then(() => {
        staffDelete(row.id)
          .then(res => {
            row.status = parseInt(row.status) === 0 ? 1 : 0
            if (row.status === 0) row.work_status = 2
            const msg = (res && res.msg) || ''
            this.$message.success(msg || act + '成功')
          })
          .catch(() => {})
      }).catch(() => {})
    },
    /* ===== 二维码 ===== */
    openQrcode(row) {
      this.curStaff = row
      this.qrcodeVisible = true
      this.qrcodeLoading = true
      staffQrcodeList(row.id)
        .then(data => { this.qrcodeList = data.list || [] })
        .catch(() => { this.qrcodeList = [] })
        .finally(() => { this.qrcodeLoading = false })
    },
    openQrcodeEdit(q) {
      if (q) {
        this.qform = {
          id: q.id, type: parseInt(q.type), account: q.account,
          qrcode: q.qrcode, status: parseInt(q.status) === 1, remark: q.remark || ''
        }
      } else {
        this.qform = { id: 0, type: 1, account: '', qrcode: '', status: true, remark: '' }
      }
      this.qrcodeEditVisible = true
    },
    editQrcode(q) {
      this.openQrcodeEdit(q)
    },
    onQrcodeUploadSuccess(res) {
      if (res && res.data && res.data.url) {
        this.qform.qrcode = res.data.url
        this.$message.success('上传成功')
      } else {
        this.$message.error('上传失败')
      }
    },
    /* 列表内直接上传：按所选类型 + 账号 上传图片即新增一条 */
    onQuickQrcodeSuccess(res) {
      if (!res || !res.data || !res.data.url) {
        this.$message.error('上传失败')
        return
      }
      if (this.quickQrcodeSaving) return
      this.quickQrcodeSaving = true
      staffQrcodeSave({
        staff_id: this.curStaff.id,
        type: parseInt(this.quickQrcodeForm.type) === 1 ? 1 : 2,
        account: (this.quickQrcodeForm.account || '').trim(),
        qrcode: res.data.url,
        status: 1,
        remark: ''
      })
        .then(() => {
          this.$message.success('二维码已添加')
          this.quickQrcodeForm.account = ''
          this.openQrcode(this.curStaff)
        })
        .catch(() => {})
        .finally(() => { this.quickQrcodeSaving = false })
    },
    onQrcodeStatusChange(q, val) {
      const status = val ? 1 : 0
      staffQrcodeStatus({ id: q.id, status })
        .then(() => {
          q.status = status
          this.$message.success(status === 1 ? '已启用' : '已停用')
        })
        .catch(() => { this.$forceUpdate() })
    },
    doQrcodeSave() {
      this.$refs.qform.validate(valid => {
        if (!valid) return
        this.qrcodeSaving = true
        staffQrcodeSave({
          id: this.qform.id,
          staff_id: this.curStaff.id,
          type: this.qform.type,
          account: this.qform.account,
          qrcode: this.qform.qrcode,
          status: this.qform.status ? 1 : 0,
          remark: (this.qform.remark || '').trim()
        })
          .then(() => {
            this.$message.success(this.qform.id ? '修改成功' : '新增成功')
            this.qrcodeEditVisible = false
            this.openQrcode(this.curStaff)
          })
          .catch(() => {})
          .finally(() => { this.qrcodeSaving = false })
      })
    },
    deleteQrcode(q) {
      this.$confirm(`确认删除该${q.type_text}二维码？`, '删除二维码', {
        confirmButtonText: '删 除',
        cancelButtonText: '取 消',
        type: 'warning'
      }).then(() => {
        staffQrcodeDelete(q.id)
          .then(() => {
            this.$message.success('删除成功')
            this.openQrcode(this.curStaff)
          })
          .catch(() => {})
      }).catch(() => {})
    },
    /* ===== 更改小组 ===== */
    openGroup(row) {
      this.curStaff = row
      this.gform = { unit_id: parseInt(row.unit_id), team_id: parseInt(row.team_id) }
      this.groupTeams = this.teams.filter(t => parseInt(t.unit_id) === parseInt(row.unit_id))
      this.groupVisible = true
    },
    onGroupUnitChange() {
      this.gform.team_id = null
      this.groupTeams = this.teams.filter(t => parseInt(t.unit_id) === parseInt(this.gform.unit_id))
    },
    doGroupSave() {
      this.$refs.gform.validate(valid => {
        if (!valid) return
        this.saving = true
        staffSave({
          id: this.curStaff.id,
          unit_id: this.gform.unit_id,
          team_id: this.gform.team_id,
          account: this.curStaff.account,
          name: this.curStaff.name,
          work_status: parseInt(this.curStaff.work_status),
          status: parseInt(this.curStaff.status)
        })
          .then(() => {
            this.$message.success('小组已更改')
            this.groupVisible = false
            this.load()
          })
          .catch(() => {})
          .finally(() => { this.saving = false })
      })
    },
    /* ===== 重置账号 ===== */
    openAccount(row) {
      this.curStaff = row
      this.rform = { account: row.account }
      this.accountVisible = true
    },
    doAccountSave() {
      this.$refs.rform.validate(valid => {
        if (!valid) return
        this.saving = true
        staffSave({
          id: this.curStaff.id,
          unit_id: parseInt(this.curStaff.unit_id),
          team_id: parseInt(this.curStaff.team_id),
          account: this.rform.account,
          name: this.curStaff.name,
          work_status: parseInt(this.curStaff.work_status),
          status: parseInt(this.curStaff.status)
        })
          .then(() => {
            this.$message.success('账号已重置')
            this.accountVisible = false
            this.load()
          })
          .catch(() => {})
          .finally(() => { this.saving = false })
      })
    },
    /* ===== 重置密码 ===== */
    openPassword(row) {
      this.curStaff = row
      this.pform = { password: '' }
      this.passwordVisible = true
    },
    doPasswordSave() {
      this.$refs.pform.validate(valid => {
        if (!valid) return
        this.saving = true
        staffSave({
          id: this.curStaff.id,
          unit_id: parseInt(this.curStaff.unit_id),
          team_id: parseInt(this.curStaff.team_id),
          account: this.curStaff.account,
          name: this.curStaff.name,
          password: this.pform.password,
          work_status: parseInt(this.curStaff.work_status),
          status: parseInt(this.curStaff.status)
        })
          .then(() => {
            this.$message.success('密码已重置')
            this.passwordVisible = false
          })
          .catch(() => {})
          .finally(() => { this.saving = false })
      })
    },
    /* ===== 工具 ===== */
    fmtDateTime(ts) {
      if (!ts) return '—'
      const d = new Date(parseInt(ts) * 1000)
      const p = n => (n < 10 ? '0' + n : '' + n)
      return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`
    }
  }
}
</script>

<style scoped>
.staff-page { padding: 0; }

.tool-card {
  margin-bottom: 14px;
  border-radius: 10px;
  border: 1px solid #E5E9F2;
}

.tool-row { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 10px; }
.tool-left { display: flex; align-items: baseline; gap: 12px; }
.tool-right { display: flex; gap: 10px; flex-wrap: wrap; }
.tool-title { font-size: 15px; font-weight: 700; color: #18233C; }
.tool-sub { font-size: 12px; color: #8A94AD; }

.table-card { border-radius: 10px; border: 1px solid #E5E9F2; }

.cell-name-main { min-width: 0; }
.cell-name-text { display: block; font-size: 13px; font-weight: 600; color: #18233C; }
.cell-name-sub { display: block; font-size: 11px; color: #8A94AD; margin-top: 2px; font-family: monospace; }
.cell-name-meta { display: flex; align-items: center; gap: 4px; margin-top: 3px; font-size: 11px; color: #A8B2C9; }
.cell-name-arrow { font-size: 10px; color: #C3CADB; }
.cell-time { font-size: 12px; color: #5A6580; font-variant-numeric: tabular-nums; }
.del-btn { color: #E5484D; }
.en-btn { color: #16A34A; }
.form-hint { margin-top: 6px; font-size: 12px; color: #8A94AD; }

/* 今日接单 */
.today-count {
  font-size: 13px;
  color: #5A6580;
  font-variant-numeric: tabular-nums;
}
.today-count b {
  font-size: 15px;
  font-weight: 700;
  color: #3668FC;
}
.today-slash { color: #C3CADB; margin: 0 2px; }
.today-none { color: #B4BDCF; }

/* 工作状态：状态标签 + 开关 */
.work-status-cell { display: inline-flex; align-items: center; gap: 8px; }

/* 每日接受推广上限 */
.promo-limit-cell { display: inline-flex; align-items: center; gap: 6px; }
.promo-limit-cell :deep(.el-input__inner) { text-align: center; }
.promo-limit-flag { font-size: 11px; color: #8A94AD; }

/* 二维码弹窗 */
.qrcode-note {
  background: #EDF3FE;
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 12px;
  color: #3668FC;
  margin-bottom: 12px;
}

.qrcode-body { max-height: 420px; overflow-y: auto; display: flex; flex-wrap: wrap; gap: 14px; }

.qrcode-item {
  width: 150px;
  border: 1px solid #E5E9F2;
  border-radius: 10px;
  padding: 10px;
  text-align: center;
}

.qrcode-card { display: flex; flex-direction: column; align-items: center; }

.qrcode-img {
  width: 110px;
  height: 110px;
  object-fit: contain;
  border-radius: 8px;
  background: #F7F9FC;
  border: 1px solid #F0F2F6;
}

.qrcode-type {
  margin-top: 8px;
  font-size: 12px;
  font-weight: 700;
  color: #3668FC;
  background: #EDF3FE;
  border-radius: 4px;
  padding: 2px 8px;
}

.qtype-2 { color: #16A34A; background: #E8F7EE; }

.qrcode-account {
  margin-top: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #18233C;
  word-break: break-all;
}

.qrcode-remark {
  margin-top: 4px;
  font-size: 11px;
  color: #8A94AD;
  word-break: break-all;
}

.qrcode-ops { margin-top: 8px; }

.empty-row { padding: 30px 0; text-align: center; font-size: 12px; color: #B4BDCF; width: 100%; }

/* 列表内直接上传区：类型 + 账号 + 上传 */
.qrcode-inline-upload {
  margin-top: 14px;
  border: 1px dashed #C3CADB;
  border-radius: 10px;
  background: #FAFBFF;
  padding: 14px 16px;
}
.qrcode-inline-upload :deep(.el-upload) { display: inline-block; }
.qrcode-inline-form {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 14px 18px;
}
.qrcode-inline-item {
  display: flex;
  align-items: center;
  gap: 8px;
}
.qrcode-inline-label {
  font-size: 13px;
  color: #5A6580;
  flex-shrink: 0;
}

/* 二维码上传 */
.qrcode-uploader {
  width: 100%;
}

.qrcode-uploader :deep(.el-upload) {
  width: 120px;
  height: 120px;
  border: 1px dashed #C3CADB;
  border-radius: 8px;
  cursor: pointer;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #FAFBFF;
  transition: border-color 0.2s;
}

.qrcode-uploader :deep(.el-upload:hover) { border-color: #3668FC; }

.qrcode-upload-icon { font-size: 26px; color: #8A94AD; }

.qrcode-upload-preview { width: 100%; height: 100%; object-fit: cover; display: block; }

/* ===== Element UI 品牌色统一 ===== */
::v-deep .el-button--primary { background: #3668FC; border-color: #3668FC; }
::v-deep .el-button--primary:hover,
::v-deep .el-button--primary:focus { background: #5B8DFF; border-color: #5B8DFF; }
::v-deep .el-table__row:hover > td { background: #F5F8FF !important; }
::v-deep .el-dialog { border-radius: 12px; }
</style>